import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  headerContent?: React.ReactNode;
  className?: string;
  headerClassName?: string;
  /** Colori del bottone di chiusura: il default (X bianca) vale sugli header scuri;
      gli header chiari passano una variante scura per non renderla invisibile. */
  closeButtonClassName?: string;
  /** Meno respiro attorno al contenuto: per i modali operativi, pieni di campi. */
  compact?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  headerContent,
  className = "",
  headerClassName = "bg-white",
  closeButtonClassName = "bg-black/10 hover:bg-black/20 text-white/90",
  compact = false
}) => {
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="relative z-[9999]"
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          {/* Backdrop */}
          <motion.div
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1 },
            }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"
            style={{ margin: 0 }}
          />

          {/* Modal Container */}
          <div className="fixed inset-0 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              variants={{
                hidden: { opacity: 0, scale: 0.95, y: 20 },
                visible: { opacity: 1, scale: 1, y: 0 },
              }}
              transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
              className={`bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden pointer-events-auto flex flex-col max-h-[90dvh] ${className}`}
            >
              {/* Header */}
              <div className={`relative flex-shrink-0 ${compact ? 'px-5 pt-5 pb-4' : 'p-6 md:p-8'} ${headerClassName}`}>
                <button
                  onClick={onClose}
                  className={`absolute top-4 right-4 p-2 rounded-full transition-colors z-10 ${closeButtonClassName}`}
                >
                  <X size={20} />
                </button>
                {headerContent}
              </div>

              {/* Scrollable Content */}
              <div className={`overflow-y-auto custom-scrollbar flex-1 ${compact ? 'px-5 pb-5 pt-0' : 'p-6 md:p-8 !pt-4'}`}>
                {children}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default Modal;